import {
  EVENT_LISTENER_METHODS,
  eventBus,
  MEDIA_TYPE,
  toggleAudioTrack,
  updateAudioInputDeviceState,
  useBaseStore,
  useHideMuteAudioButton,
  useIsAudioInputSelectionSupported,
  useTrackMuted,
  useTracksStore,
  useTranslation,
  type AudioInputDevice,
  type AudioOutputDevice,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../icon/Icon.web';
import Settings from '../Settings.web';

type DeviceType = 'microphone' | 'speaker';

const AudioControl: React.FC = () => {
  const muted = useTrackMuted(MEDIA_TYPE.AUDIO);
  const hideToggleAudioButton = useHideMuteAudioButton();
  const { t } = useTranslation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const hasAudioPermission = useTracksStore(state => state.permissions.audio);

  const isAudioInputSelectionSupported = useIsAudioInputSelectionSupported();

  const [selectedDeviceType, setSelectedDeviceType] =
    useState<DeviceType>('microphone');

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Mock input devices (replace with real media devices if needed)
  const audioInputDevices = useBaseStore(state => state.audioInputDevices);

  // Mock output devices
  const audioOutputDevices = useBaseStore(state => state.audioOutputDevices);

  const audioInputDevice = useBaseStore(state => state.audioInputDevice);
  const audioOutputDevice = useBaseStore(state => state.audioOutputDevice);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (isDropdownOpen || isMenuOpen)) {
        setIsDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    if (isDropdownOpen || isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDropdownOpen, isMenuOpen]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
    setIsMenuOpen(false);
  }, []);

  const toggleMenu = useCallback((deviceType: DeviceType) => {
    setIsMenuOpen(prev => !prev);
    setSelectedDeviceType(deviceType);
  }, []);
  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, []);

  const handleMicDeviceSelect = useCallback((device: MediaDeviceInfo) => {
    updateAudioInputDeviceState(device as AudioInputDevice);
    setIsMenuOpen(false);
  }, []);

  const handleSpeakerDeviceSelect = useCallback((device: MediaDeviceInfo) => {
    useBaseStore.setState({
      audioOutputDevice: device as AudioOutputDevice,
    });
    setIsMenuOpen(false);
  }, []);

  const onAudioToggleClick = useCallback(() => {
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.ButtonClickListener
        .onToggleAudioButtonClicked,
    });
    toggleAudioTrack();
  }, []);

  if (hideToggleAudioButton) {
    return null;
  }

  const audioDeviceSelector = (
    icon: 'mic-fill' | 'speaker-fill',
    deviceType: DeviceType,
  ) => {
    const selectedDevice =
      deviceType === 'microphone' ? audioInputDevice : audioOutputDevice;
    const isCurrentDeviceType = selectedDeviceType === deviceType;

    if (!selectedDevice) {
      return null;
    }

    return (
      <div className="cometchat-calls-audio-selector">
        <button
          className="cometchat-calls-audio-selector__button"
          onClick={() => toggleMenu(deviceType)}
          aria-expanded={isMenuOpen && isCurrentDeviceType}
          aria-haspopup="listbox">
          <div className="cometchat-calls-audio-selector__content">
            <Icon
              name={icon}
              fill="var(--cometchat-calls-static-white)"
              size={24}
            />
            <span className="cometchat-calls-audio-selector__device-label">
              {selectedDevice.label}
            </span>
            <div
              className={clsx('cometchat-calls-audio-selector__arrow', {
                'cometchat-calls-audio-selector__arrow--open':
                  isMenuOpen && isCurrentDeviceType,
              })}>
              <Icon
                name="arrow-up"
                fill="var(--cometchat-calls-static-white)"
                size={24}
              />
            </div>
          </div>
        </button>
      </div>
    );
  };

  const currentDevices =
    selectedDeviceType === 'microphone'
      ? audioInputDevices
      : audioOutputDevices;
  const currentSelectedDevice =
    selectedDeviceType === 'microphone' ? audioInputDevice : audioOutputDevice;
  const handleDeviceSelect =
    selectedDeviceType === 'microphone'
      ? handleMicDeviceSelect
      : handleSpeakerDeviceSelect;

  return (
    <div className="cometchat-calls-audio-control" ref={dropdownRef}>
      {!hasAudioPermission && (
        <div className="cometchat-calls-audio-control__warning">
          <Icon
            name="alert"
            fill="var(--cometchat-calls-warning-color)"
            size={20}
          />
        </div>
      )}
      <Settings visible={isSettingsOpen} onClose={toggleSettings} />
      {isDropdownOpen && (
        <div className="cometchat-calls-audio-control__dropdown-wrapper">
          {isMenuOpen && (
            <div
              className={clsx('cometchat-calls-audio-control__device-menu', {
                'cometchat-calls-audio-control__device-menu-speaker':
                  selectedDeviceType == 'speaker',
              })}
              role="listbox">
              {currentDevices.map(device => (
                <div
                  className={clsx(
                    'cometchat-calls-audio-control__device-menu-item',
                    {
                      'cometchat-calls-audio-control__device-menu-item--selected':
                        device.deviceId === currentSelectedDevice?.deviceId,
                    },
                  )}
                  onClick={() => handleDeviceSelect(device)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDeviceSelect(device);
                    }
                  }}
                  key={device.deviceId}
                  role="option"
                  aria-selected={
                    device.deviceId === currentSelectedDevice?.deviceId
                  }
                  tabIndex={0}>
                  <div className="cometchat-calls-audio-control__device-menu-icon">
                    {device.deviceId === currentSelectedDevice?.deviceId ? (
                      <Icon
                        name="check-fill"
                        fill="var(--cometchat-calls-static-white)"
                        size={24}
                      />
                    ) : null}
                  </div>
                  <span className="cometchat-calls-audio-control__device-menu-label">
                    {device.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="cometchat-calls-audio-control__dropdown-bar">
            {audioInputDevice && (
              <div className="cometchat-calls-audio-control__section">
                {audioDeviceSelector('mic-fill', 'microphone')}
              </div>
            )}

            {audioOutputDevice && (
              <div className="cometchat-calls-audio-control__section">
                {audioDeviceSelector('speaker-fill', 'speaker')}
              </div>
            )}

            <div className="cometchat-calls-audio-control__section cometchat-calls-audio-control__section--settings">
              <button
                className="cometchat-calls-audio-control__settings-button"
                onClick={toggleSettings}
                aria-label={t('controlPanel.audioControl.audioSettings')}
                title={t('controlPanel.audioControl.audioSettings')}>
                <Icon
                  name="settings"
                  fill="var(--cometchat-calls-static-white)"
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={clsx('cometchat-calls-audio-control__wrapper', {
          'cometchat-calls-audio-control__wrapper--active': muted,
        })}>
        <div
          className="cometchat-calls-audio-control__button-wrapper"
          onClick={onAudioToggleClick}
          data-testid="cometchat-audio-toggle-button"
          aria-label={
            muted
              ? t('controlPanel.audioControl.unmuteMicrophone')
              : t('controlPanel.audioControl.muteMicrophone')
          }>
          <button
            className="cometchat-calls-audio-control__button"
            title={
              muted
                ? t('controlPanel.audioControl.unmuteMicrophone')
                : t('controlPanel.audioControl.muteMicrophone')
            }>
            <Icon
              name={muted ? 'mic-off-fill' : 'mic-fill'}
              fill={
                muted
                  ? 'var(--cometchat-calls-error-color)'
                  : 'var(--cometchat-calls-static-white)'
              }
            />
          </button>
        </div>
        <div
          className="cometchat-calls-audio-control__divider"
          onClick={onAudioToggleClick}
        />
        <div
          className="cometchat-calls-audio-control__arrow-wrapper"
          onClick={isAudioInputSelectionSupported ? toggleDropdown : undefined}
          aria-label={t('controlPanel.audioControl.audioDeviceOptions')}
          aria-expanded={isDropdownOpen}
          aria-disabled={!isAudioInputSelectionSupported}>
          <button
            disabled={!isAudioInputSelectionSupported}
            className={clsx('cometchat-calls-audio-control__arrow-button', {
              'cometchat-calls-audio-control__arrow-button-open':
                isDropdownOpen,
            })}
            title={t('controlPanel.audioControl.audioDeviceOptions')}>
            <Icon
              name="arrow-up"
              fill="var(--cometchat-calls-static-white)"
              size={16}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioControl;
